import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Drama } from '@/mocks/dramas';

interface HeroCarouselProps {
  dramas: Drama[];
}

export default function HeroCarousel({ dramas }: HeroCarouselProps) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (dramas.length <= 1) return;
    const timer = setInterval(() => {
      setCurrent((prev) => (prev + 1) % dramas.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [dramas.length]);

  if (dramas.length === 0) {
    return (
      <section className="relative flex h-[420px] md:h-[600px] w-full items-center justify-center bg-background-900">
        <div className="text-center">
          <i className="ri-movie-2-line text-5xl text-foreground-600" />
          <p className="mt-4 text-foreground-400">暂无推荐短剧</p>
        </div>
      </section>
    );
  }

  const active = dramas[current];

  return (
    <section className="relative h-[460px] md:h-[600px] w-full overflow-hidden">
      {dramas.map((drama, index) => (
        <div
          key={drama.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={drama.gallery[0]}
            alt={`${drama.title} 剧照`}
            title={`${drama.title} - 成人短剧精选推荐`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-transparent to-background-950/40" />
        </div>
      ))}

      <div className="relative z-10 flex h-full w-full items-center">
        <div className="w-full px-4 md:px-8">
          <div className="max-w-2xl">
            <div key={active.id} className="animate-fade-up">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-primary-600 px-3 py-1 text-xs font-semibold text-background-50">
                  <i className="ri-fire-fill" /> 精选推荐
                </span>
                <span className="inline-flex items-center gap-1 rounded-md bg-background-900/70 backdrop-blur px-2.5 py-1 text-xs font-semibold text-accent-300">
                  <i className="ri-star-fill text-[10px]" /> {active.rating.toFixed(1)}
                </span>
                <span className="text-xs text-foreground-300">
                  {active.year} · {active.region} · {active.genres.join(' / ')}
                </span>
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-foreground-50 font-heading tracking-tight">
                {active.title}
              </h1>
              <p className="mt-4 text-sm md:text-base text-foreground-300 leading-relaxed line-clamp-3 md:line-clamp-4">
                {active.synopsis}
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Link
                  to={`/detail/${active.id}`}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg" />
                  查看详情
                </Link>
                <Link
                  to="/category"
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-background-600 bg-background-900/60 backdrop-blur px-6 py-3 text-sm font-medium text-foreground-100 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-grid-line text-lg" />
                  浏览全部短剧
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-6 right-4 md:right-8 z-20 flex items-center gap-2">
        <button
          type="button"
          onClick={() => setCurrent((prev) => (prev - 1 + dramas.length) % dramas.length)}
          aria-label="上一张"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-600 bg-background-900/70 backdrop-blur text-foreground-200 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-lg" />
        </button>
        <button
          type="button"
          onClick={() => setCurrent((prev) => (prev + 1) % dramas.length)}
          aria-label="下一张"
          className="flex h-9 w-9 items-center justify-center rounded-full border border-background-600 bg-background-900/70 backdrop-blur text-foreground-200 hover:border-primary-500 hover:text-primary-300 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-right-s-line text-lg" />
        </button>
      </div>

      <div className="absolute bottom-6 left-4 md:left-8 z-20 flex items-center gap-2">
        {dramas.map((drama, index) => (
          <button
            key={drama.id}
            type="button"
            onClick={() => setCurrent(index)}
            aria-label={`切换到 ${drama.title}`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              index === current ? 'w-8 bg-primary-500' : 'w-3 bg-background-600 hover:bg-foreground-500'
            }`}
          />
        ))}
      </div>
    </section>
  );
}